import React, { useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  FiltersRow,
  FilterGroup,
  FilterLabel,
  SaveBtn,
} from "../styles";
import TagMultiSelect from "./TagMultiSelect";
import KpiCascadeFilter, { kpiMatchesCascadeSteps } from "../../../DataManagment/Quantative/component/KpiTagFilter";
import { PERIOD_TYPE_LABELS } from "../hooks/useTriggers";

// ─── Styles ───────────────────────────────────────────────────────────────────
const Wrapper = styled.div``;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  background: #3f88a5;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #2e6d88; }
`;

const FormCard = styled.div`
  margin-top: 20px;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 20px 24px;
  background: #fff;
`;

const StepLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #3f88a5;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 10px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f1f5f9;
  margin: 18px 0;
`;

const StepRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const StepCol = styled.div`
  flex: 1 1 220px;
  min-width: 180px;
`;

const CommonValuesRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const ApplyAllBtn = styled.button`
  padding: 7px 14px;
  background: #f1f5f9;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #3f88a5;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
  &:hover { background: #e0f2fe; border-color: #3f88a5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ScrollableTable = styled.div`
  margin-top: 20px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  max-height: 380px;
  overflow-y: scroll;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar { width: 6px; height: 6px; }
  &::-webkit-scrollbar-track { background: #f8fafc; border-radius: 10px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Thead = styled.thead`
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Th = styled.th`
  padding: 9px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 1.5px solid #e2e8f0;
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:not(:last-child) td { border-bottom: 1px solid #f1f5f9; }
`;

const Td = styled.td`
  padding: 8px 12px;
  color: #1e293b;
  vertical-align: middle;
`;

const ExistingBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  background: #dcfce7;
  color: #166534;
  padding: 2px 7px;
  border-radius: 20px;
  margin-left: 6px;
`;

const RequiredMark = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const ValidationMsg = styled.p`
  font-size: 12px;
  color: #ef4444;
  margin: 8px 0 0;
`;

const SuccessMsg = styled.p`
  font-size: 12px;
  color: #16a34a;
  margin: 8px 0 0;
`;

const CancelBtn = styled.button`
  padding: 8px 18px;
  background: transparent;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: #94a3b8; color: #1e293b; }
`;

const EditBtn = styled.button`
  padding: 3px 10px;
  background: #f1f5f9;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #3f88a5;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover { background: #e0f2fe; border-color: #3f88a5; }
`;

// ─── Modal styles ─────────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px 28px 28px;
  width: 500px;
  max-width: 95vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
`;

const ModalTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ModalSubtitle = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0 0 20px;
`;

const ModalField = styled.div`
  margin-bottom: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

// ─── Pill email input styles ──────────────────────────────────────────────────
const PillInputWrap = styled.div`
  display: flex;
  flex-wrap: ${({ $wide }) => ($wide ? "wrap" : "nowrap")};
  gap: 4px;
  align-items: ${({ $wide }) => ($wide ? "flex-start" : "center")};
  border: 1.5px solid ${({ $error }) => ($error ? "#ef4444" : "#e2e8f0")};
  border-radius: 9px;
  padding: 5px 8px;
  min-height: 36px;
  max-height: ${({ $wide }) => ($wide ? "none" : "36px")};
  max-width: ${({ $wide }) => ($wide ? "none" : "170px")};
  overflow-x: ${({ $wide }) => ($wide ? "hidden" : "auto")};
  overflow-y: ${({ $wide }) => ($wide ? "auto" : "hidden")};
  background: #fff;
  cursor: text;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar { height: 3px; width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  &:focus-within {
    border-color: ${({ $error }) => ($error ? "#ef4444" : "#93c5fd")};
    box-shadow: 0 0 0 3px ${({ $error }) => ($error ? "rgba(239,68,68,0.15)" : "rgba(63,136,165,0.12)")};
  }
`;

const PillErrorText = styled.p`
  margin: 4px 2px 0;
  font-size: 11px;
  color: #ef4444;
`;

const PillItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  background: #e0f2fe;
  color: #0369a1;
  white-space: nowrap;
  flex-shrink: 0;
`;

const PillRemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #0369a1;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  display: flex;
  align-items: center;
  opacity: 0.6;
  &:hover { opacity: 1; color: #dc2626; }
`;

const EmailTextInput = styled.input`
  border: none;
  outline: none;
  font-size: 12px;
  color: #1e293b;
  background: transparent;
  min-width: 80px;
  flex: 1 1 80px;
  padding: 1px 0;
  &::placeholder { color: #94a3b8; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// ─── PillEmailInput ───────────────────────────────────────────────────────────
const PillEmailInput = ({ value = [], onChange, placeholder, wide }) => {
  const [inputVal, setInputVal] = useState("");
  const [error, setError]       = useState("");

  const commit = () => {
    const email = inputVal.trim();
    if (!email) { setError(""); return; }
    if (!isValidEmail(email)) {
      setError(`"${email}" is not a valid email`);
      return;
    }
    if (!value.includes(email)) onChange([...value, email]);
    setInputVal("");
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !inputVal && value.length > 0) {
      onChange(value.slice(0, -1));
      setError("");
    }
  };

  return (
    <>
      <PillInputWrap
        $wide={wide}
        $error={!!error}
        onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
      >
        {value.map((email) => (
          <PillItem key={email}>
            {email}
            <PillRemoveBtn
              type="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(value.filter((v) => v !== email)); }}
            >
              ×
            </PillRemoveBtn>
          </PillItem>
        ))}
        <EmailTextInput
          value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); if (error) setError(""); }}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          placeholder={value.length === 0 ? placeholder : ""}
        />
      </PillInputWrap>
      {error && <PillErrorText>{error}</PillErrorText>}
    </>
  );
};

// ─── EmailSummary — compact cell display ──────────────────────────────────────
const EmailSummary = ({ emails }) => {
  if (!emails.length) return <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>;
  return (
    <span style={{ fontSize: 12, color: "#1e293b" }}>
      {emails[0]}
      {emails.length > 1 && (
        <span style={{ color: "#64748b", fontSize: 11, marginLeft: 4 }}>
          +{emails.length - 1}
        </span>
      )}
    </span>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const EmailConfigureTab = ({ hook }) => {
  const {
    sources,
    triggers,
    questions,
    financialYears,
    selectedFinancialYear,
    handleFinancialYearChange,
    ecfg_periodType, setEcfg_periodType,
    ecfg_selectedPeriods, setEcfg_selectedPeriods,
    ecfg_sourceIds, setEcfg_sourceIds,
    ecfg_questionIds, setEcfg_questionIds,
    bulkUpdateEmailRecipients,
    getTriggers,
    getEcfg_availablePeriodTypes,
    getEcfg_availablePeriodSlots,
    getEcfg_availableSources,
    getEcfg_availableQuestions,
  } = hook;

  const [filterSteps, setFilterSteps]     = useState([]);
  const [formOpen, setFormOpen]           = useState(false);
  const [saving, setSaving]               = useState(false);
  const [saveProgress, setSaveProgress]   = useState(0);
  const [validationMsg, setValidationMsg] = useState("");
  const [successMsg, setSuccessMsg]       = useState("");
  const [rowOverrides, setRowOverrides]   = useState({});
  const [selectedKeys, setSelectedKeys]   = useState(new Set());
  const [commonTo, setCommonTo]           = useState([]);
  const [commonCc, setCommonCc]           = useState([]);
  const [commonBcc, setCommonBcc]         = useState([]);

  // Modal state
  const [modalQKey, setModalQKey]   = useState(null);
  const [modalDraft, setModalDraft] = useState({ to: [], cc: [], bcc: [] });

  const selectAllRef = useRef(null);
  const didAutoSelectKpisRef = useRef(false);

  // ── Available option lists ─────────────────────────────────────────────────
  const availableEcfgPeriodTypes = useMemo(
    () => getEcfg_availablePeriodTypes(),
    [getEcfg_availablePeriodTypes]
  );

  const availableEcfgPeriodSlots = useMemo(
    () => getEcfg_availablePeriodSlots(ecfg_periodType),
    [getEcfg_availablePeriodSlots, ecfg_periodType]
  );

  const availableEcfgSources = useMemo(
    () => getEcfg_availableSources(ecfg_selectedPeriods),
    [getEcfg_availableSources, ecfg_selectedPeriods]
  );

  const availableEcfgQuestions = useMemo(
    () => getEcfg_availableQuestions(ecfg_selectedPeriods, ecfg_sourceIds),
    [getEcfg_availableQuestions, ecfg_selectedPeriods, ecfg_sourceIds]
  );

  const kpiListForFilter = useMemo(
    () => availableEcfgQuestions.map(q => ({ ...q, id: q.questionId, rawTags: q.tags })),
    [availableEcfgQuestions]
  );

  const availableEcfgQuestionsFiltered = useMemo(() => {
    const active = filterSteps.filter(s => s.values?.size);
    if (!active.length) return availableEcfgQuestions;
    return kpiListForFilter.filter(q => kpiMatchesCascadeSteps(q, filterSteps));
  }, [availableEcfgQuestions, kpiListForFilter, filterSteps]);

  const selectedQuestions = useMemo(
    () => questions.filter((q) => ecfg_questionIds.includes(q.questionId)),
    [questions, ecfg_questionIds]
  );

  const selectedSources = useMemo(
    () => sources.filter((s) => ecfg_sourceIds.includes(s.id)),
    [sources, ecfg_sourceIds]
  );

  // One row per selected question; merge existing email data across all
  // selected (source × period) combinations for display.
  const questionRows = useMemo(() => {
    return selectedQuestions.map((q) => {
      const key = String(q.questionId);
      const existingTriggers = triggers.filter(
        (t) =>
          t.questionId === q.questionId &&
          String(t.financialYearId) === String(selectedFinancialYear) &&
          (ecfg_selectedPeriods.length === 0 ||
            ecfg_selectedPeriods.some((p) => p.fromDate === t.fromDate)) &&
          (ecfg_sourceIds.length === 0 || ecfg_sourceIds.includes(t.sourceId))
      );
      const mergedTo  = [...new Set(existingTriggers.flatMap((t) => t.emailRecipients?.to  ?? []))];
      const mergedCc  = [...new Set(existingTriggers.flatMap((t) => t.emailRecipients?.cc  ?? []))];
      const mergedBcc = [...new Set(existingTriggers.flatMap((t) => t.emailRecipients?.bcc ?? []))];
      const hasExisting = existingTriggers.some((t) => t.emailRecipients != null);
      return { q, key, hasExisting, mergedTo, mergedCc, mergedBcc };
    });
  }, [selectedQuestions, triggers, selectedFinancialYear, ecfg_selectedPeriods, ecfg_sourceIds]);

  // Sync selectedKeys: new rows auto-checked, stale keys pruned.
  useEffect(() => {
    const currentKeys = new Set(questionRows.map((r) => r.key));
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((k) => currentKeys.has(k)));
      for (const k of currentKeys) pruned.add(k);
      return pruned;
    });
  }, [questionRows]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      selectedKeys.size > 0 && selectedKeys.size < questionRows.length;
  }, [selectedKeys, questionRows]);

  // Prune stale IDs when available options shrink.
  useEffect(() => {
    const validIds = availableEcfgSources.map((s) => s.id);
    setEcfg_sourceIds((prev) => prev.filter((id) => validIds.includes(id)));
  }, [availableEcfgSources]);

  useEffect(() => {
    const validIds = availableEcfgQuestions.map((q) => q.questionId);
    setEcfg_questionIds((prev) => prev.filter((id) => validIds.includes(id)));
  }, [availableEcfgQuestions]);

  // Auto-select all KPIs after a location is picked; reset the guard when none are picked.
  useEffect(() => {
    if (ecfg_sourceIds.length === 0) {
      didAutoSelectKpisRef.current = false;
      return;
    }
    if (didAutoSelectKpisRef.current || availableEcfgQuestions.length === 0) return;
    setEcfg_questionIds(availableEcfgQuestions.map((q) => q.questionId));
    didAutoSelectKpisRef.current = true;
  }, [ecfg_sourceIds, availableEcfgQuestions, setEcfg_questionIds]);

  // ── Email field helpers ───────────────────────────────────────────────────
  const getRowField = (key, row, field) => {
    if (rowOverrides[key]?.[field] !== undefined) return rowOverrides[key][field];
    if (field === "to")  return row.mergedTo;
    if (field === "cc")  return row.mergedCc;
    if (field === "bcc") return row.mergedBcc;
    return [];
  };

  const handleRowChange = (key, field, emails) =>
    setRowOverrides((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), [field]: emails } }));

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleKey = (key) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleSelectAll = () => {
    if (selectedKeys.size === questionRows.length) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(questionRows.map((r) => r.key)));
  };

  // ── Modal helpers ─────────────────────────────────────────────────────────
  const openModal = (key) => {
    const row = questionRows.find((r) => r.key === key);
    if (!row) return;
    setModalDraft({
      to:  getRowField(key, row, "to"),
      cc:  getRowField(key, row, "cc"),
      bcc: getRowField(key, row, "bcc"),
    });
    setModalQKey(key);
  };

  const handleModalSave = () => {
    setRowOverrides((prev) => ({
      ...prev,
      [modalQKey]: { to: modalDraft.to, cc: modalDraft.cc, bcc: modalDraft.bcc },
    }));
    setModalQKey(null);
  };

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handlePeriodTypeChange = (e) => {
    setEcfg_periodType(e.target.value);
    setEcfg_selectedPeriods([]);
    setEcfg_sourceIds([]);
    setEcfg_questionIds([]);
  };

  const togglePeriod = (fromDate) => {
    setEcfg_selectedPeriods((prev) => {
      const exists = prev.some((p) => p.fromDate === fromDate);
      if (exists) return prev.filter((p) => p.fromDate !== fromDate);
      const slot = availableEcfgPeriodSlots.find((s) => s.fromDate === fromDate);
      return slot ? [...prev, slot] : prev;
    });
  };

  const handleApplyAll = () => {
    const overrides = {};
    for (const row of questionRows) {
      overrides[row.key] = {
        to:  commonTo.length  ? commonTo  : getRowField(row.key, row, "to"),
        cc:  commonCc.length  ? commonCc  : getRowField(row.key, row, "cc"),
        bcc: commonBcc.length ? commonBcc : getRowField(row.key, row, "bcc"),
      };
    }
    setRowOverrides(overrides);
  };

  const handleClearAll = () => {
    const overrides = {};
    for (const row of questionRows) {
      overrides[row.key] = { to: row.mergedTo, cc: row.mergedCc, bcc: row.mergedBcc };
    }
    setRowOverrides(overrides);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!ecfg_periodType)              return "Please select a period type.";
    if (!ecfg_selectedPeriods.length)  return "Please select at least one period.";
    if (!ecfg_sourceIds.length)        return "Please select at least one location.";
    if (!ecfg_questionIds.length)      return "Please select at least one KPI.";
    if (selectedKeys.size === 0)       return "Please select at least one row to save.";

    for (const row of questionRows) {
      if (!selectedKeys.has(row.key)) continue;
      const toList  = getRowField(row.key, row, "to");
      const ccList  = getRowField(row.key, row, "cc");
      const bccList = getRowField(row.key, row, "bcc");
      if ((ccList.length > 0 || bccList.length > 0) && toList.length === 0) {
        return `"To" is required when CC or BCC are set (${row.q.title}).`;
      }
      for (const email of [...toList, ...ccList, ...bccList]) {
        if (!isValidEmail(email)) return `Invalid email: "${email}"`;
      }
    }
    return "";
  };

  // ── Save — expands each question row to all (source × period) payloads ────
  const handleSave = async () => {
    setValidationMsg("");
    setSuccessMsg("");
    const err = validate();
    if (err) { setValidationMsg(err); return; }

    const payloads = [];
    for (const row of questionRows) {
      if (!selectedKeys.has(row.key)) continue;
      const to  = getRowField(row.key, row, "to");
      const cc  = getRowField(row.key, row, "cc");
      const bcc = getRowField(row.key, row, "bcc");
      for (const s of selectedSources) {
        for (const p of ecfg_selectedPeriods) {
          payloads.push({
            financialYearId: Number(selectedFinancialYear),
            questionId:      row.q.questionId,
            questionType:    "quantitative",
            sourceId:        s.id,
            fromDate:        p.fromDate,
            toDate:          p.toDate,
            emailRecipients: { to, cc, bcc },
          });
        }
      }
    }

    setSaving(true);
    setSaveProgress(0);
    const ok = await bulkUpdateEmailRecipients(payloads, setSaveProgress);
    setSaving(false);

    if (ok) {
      const kpiCount = questionRows.filter((r) => selectedKeys.has(r.key)).length;
      setSuccessMsg(
        `${kpiCount} KPI${kpiCount > 1 ? "s" : ""} updated across ${payloads.length} trigger${payloads.length > 1 ? "s" : ""}.`
      );
      await getTriggers();
      handleCancel();
    } else {
      setValidationMsg("Failed to update email recipients. Please try again.");
    }
  };

  const handleCancel = () => {
    setFormOpen(false);
    setValidationMsg("");
    setEcfg_periodType("");
    setEcfg_selectedPeriods([]);
    setEcfg_sourceIds([]);
    setEcfg_questionIds([]);
    setFilterSteps([]);
    setRowOverrides({});
    setCommonTo([]);
    setCommonCc([]);
    setCommonBcc([]);
    setModalQKey(null);
  };

  const modalRow = modalQKey ? questionRows.find((r) => r.key === modalQKey) : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Wrapper>
      {successMsg && <SuccessMsg>{successMsg}</SuccessMsg>}

      {!formOpen && (
        <AddBtn onClick={() => { setFormOpen(true); setSuccessMsg(""); }}>
          + Add Email Recipients
        </AddBtn>
      )}

      {formOpen && (
        <FormCard>

          {/* Step 1 — Financial Year */}
          <StepLabel>Step 1 — Financial Year</StepLabel>
          <FiltersRow>
            <FilterGroup style={{ maxWidth: 240 }}>
              <FilterLabel>Financial Year</FilterLabel>
              <select
                value={selectedFinancialYear}
                onChange={(e) => handleFinancialYearChange(e.target.value)}
                style={{
                  padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
                  fontSize: 13, color: "#1e293b", background: "#fff", outline: "none",
                  transition: "border-color 0.15s",
                }}
              >
                <option value="">Select year</option>
                {financialYears.map((fy) => (
                  <option key={fy.value} value={fy.value}>{fy.label}</option>
                ))}
              </select>
            </FilterGroup>
          </FiltersRow>

          {/* Step 2 — Period Type */}
          {selectedFinancialYear && (
            <>
              <Divider />
              <StepLabel>Step 2 — Period Type</StepLabel>
              <FiltersRow>
                <FilterGroup style={{ maxWidth: 240 }}>
                  <FilterLabel>Period Type</FilterLabel>
                  <select
                    value={ecfg_periodType}
                    onChange={handlePeriodTypeChange}
                    style={{
                      padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9,
                      fontSize: 13, color: "#1e293b", background: "#fff", outline: "none",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <option value="">Select type</option>
                    {availableEcfgPeriodTypes.map((val) => (
                      <option key={val} value={val}>{PERIOD_TYPE_LABELS[val]}</option>
                    ))}
                  </select>
                </FilterGroup>
              </FiltersRow>
            </>
          )}

          {/* Steps 3 + 4 */}
          {selectedFinancialYear && ecfg_periodType && (
            <>
              <Divider />
              <StepRow>
                <StepCol>
                  <StepLabel>Step 3 — Periods</StepLabel>
                  <FilterLabel>Periods ({availableEcfgPeriodSlots.length})</FilterLabel>
                  <TagMultiSelect
                    options={availableEcfgPeriodSlots}
                    selected={ecfg_selectedPeriods.map((p) => p.fromDate)}
                    getKey={(s) => s.fromDate}
                    getLabel={(s) => s.label}
                    placeholder="Select periods…"
                    onToggle={togglePeriod}
                    onSelectAll={(keys) =>
                      setEcfg_selectedPeriods(
                        keys.length === 0 ? [] : availableEcfgPeriodSlots.filter((s) => keys.includes(s.fromDate))
                      )
                    }
                  />
                </StepCol>

                <StepCol>
                  <StepLabel>Step 4 — Locations</StepLabel>
                  <FilterLabel>Locations</FilterLabel>
                  <TagMultiSelect
                    options={availableEcfgSources}
                    selected={ecfg_sourceIds}
                    getKey={(s) => s.id}
                    getLabel={(s) => s.unitCode}
                    placeholder="Select locations…"
                    onToggle={(id) =>
                      setEcfg_sourceIds((prev) =>
                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                      )
                    }
                    onSelectAll={(ids) => setEcfg_sourceIds(ids)}
                  />
                </StepCol>
              </StepRow>
            </>
          )}

          {/* Step 5 — KPIs */}
          {ecfg_sourceIds.length > 0 && (
            <>
              <Divider />
              <StepLabel>Step 5 — KPIs</StepLabel>
              <StepRow style={{ flexDirection: "column" }}>
                <StepCol style={{ flex: "none" }}>
                  <FilterLabel>Filter by Tags</FilterLabel>
                  <KpiCascadeFilter
                    kpiList={kpiListForFilter}
                    filterSteps={filterSteps}
                    onFilterStepsChange={(steps) => {
                      setFilterSteps(steps);
                      const active = steps.filter((s) => s.values?.size);
                      const next = active.length
                        ? kpiListForFilter.filter((q) => kpiMatchesCascadeSteps(q, steps))
                        : availableEcfgQuestions;
                      setEcfg_questionIds(next.map((q) => q.questionId));
                    }}
                  />
                </StepCol>

                <StepCol style={{ flex: "none" }}>
                  <FilterLabel>KPIs{filterSteps.some(s => s.values?.size) ? " (filtered)" : ""}</FilterLabel>
                  <TagMultiSelect
                    options={availableEcfgQuestionsFiltered}
                    selected={ecfg_questionIds}
                    getKey={(q) => q.questionId}
                    getLabel={(q) => q.title}
                    placeholder="Select KPIs…"
                    onToggle={(id) =>
                      setEcfg_questionIds((prev) =>
                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                      )
                    }
                    onSelectAll={(ids) => setEcfg_questionIds(ids)}
                  />
                  {availableEcfgQuestionsFiltered.length === 0 && (
                    <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                      No KPIs match the selected tags.
                    </p>
                  )}
                </StepCol>
              </StepRow>
            </>
          )}

          {/* Step 6 — Email Recipients */}
          {ecfg_questionIds.length > 0 && (
            <>
              <Divider />
              <StepLabel>Step 6 — Email Recipients</StepLabel>

              {/* Common apply-all row */}
              <CommonValuesRow>
                <FilterGroup style={{ flex: "0 0 auto" }}>
                  <FilterLabel>Common To<RequiredMark>*</RequiredMark></FilterLabel>
                  <PillEmailInput value={commonTo} onChange={setCommonTo} placeholder="user@example.com" />
                </FilterGroup>
                <FilterGroup style={{ flex: "0 0 auto" }}>
                  <FilterLabel>Common CC</FilterLabel>
                  <PillEmailInput value={commonCc} onChange={setCommonCc} placeholder="user@example.com" />
                </FilterGroup>
                <FilterGroup style={{ flex: "0 0 auto" }}>
                  <FilterLabel>Common BCC</FilterLabel>
                  <PillEmailInput value={commonBcc} onChange={setCommonBcc} placeholder="user@example.com" />
                </FilterGroup>
                <ApplyAllBtn
                  type="button"
                  onClick={handleApplyAll}
                  disabled={questionRows.length === 0}
                >
                  Apply All
                </ApplyAllBtn>
                <ApplyAllBtn
                  type="button"
                  onClick={handleClearAll}
                  disabled={questionRows.length === 0}
                  style={{ color: "#64748b" }}
                >
                  Clear All
                </ApplyAllBtn>
                <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "flex-end" }}>
                  <CancelBtn onClick={handleCancel}>Cancel</CancelBtn>
                  <SaveBtn
                    onClick={handleSave}
                    disabled={saving || questionRows.length === 0}
                    style={{ padding: "8px 18px", fontSize: 13 }}
                  >
                    {saving ? `Saving… ${saveProgress}%` : "Save Recipients"}
                  </SaveBtn>
                </div>
              </CommonValuesRow>

              {validationMsg && <ValidationMsg>{validationMsg}</ValidationMsg>}

              {/* Per-KPI table */}
              {questionRows.length > 0 && (
                <ScrollableTable>
                  <PreviewTable>
                    <Thead>
                      <tr>
                        <Th style={{ width: 36, textAlign: "center" }}>
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            checked={questionRows.length > 0 && selectedKeys.size === questionRows.length}
                            onChange={handleSelectAll}
                            style={{ cursor: "pointer", accentColor: "#3f88a5" }}
                          />
                        </Th>
                        <Th>KPI</Th>
                        <Th>To<RequiredMark>*</RequiredMark></Th>
                        <Th>CC</Th>
                        <Th>BCC</Th>
                        <Th style={{ width: 70 }}></Th>
                      </tr>
                    </Thead>
                    <tbody>
                      {questionRows.map((row) => (
                        <Tr key={row.key}>
                          <Td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(row.key)}
                              onChange={() => toggleKey(row.key)}
                              style={{ cursor: "pointer", accentColor: "#3f88a5" }}
                            />
                          </Td>
                          <Td>
                            {row.q.title}
                            {row.hasExisting && <ExistingBadge>existing</ExistingBadge>}
                          </Td>
                          <Td><EmailSummary emails={getRowField(row.key, row, "to")} /></Td>
                          <Td><EmailSummary emails={getRowField(row.key, row, "cc")} /></Td>
                          <Td><EmailSummary emails={getRowField(row.key, row, "bcc")} /></Td>
                          <Td>
                            <EditBtn type="button" onClick={() => openModal(row.key)}>
                              Edit
                            </EditBtn>
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </PreviewTable>
                </ScrollableTable>
              )}
            </>
          )}

        </FormCard>
      )}

      {/* Individual row edit modal */}
      {modalRow && (
        <ModalOverlay onClick={() => setModalQKey(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle title={modalRow.q.title}>{modalRow.q.title}</ModalTitle>
            <ModalSubtitle>
              Recipients set here apply to all selected periods and locations.
            </ModalSubtitle>

            <ModalField>
              <FilterLabel>To<RequiredMark>*</RequiredMark></FilterLabel>
              <PillEmailInput
                wide
                value={modalDraft.to}
                onChange={(to) => setModalDraft((d) => ({ ...d, to }))}
                placeholder="user@example.com — press Enter or comma to add"
              />
            </ModalField>

            <ModalField>
              <FilterLabel>CC</FilterLabel>
              <PillEmailInput
                wide
                value={modalDraft.cc}
                onChange={(cc) => setModalDraft((d) => ({ ...d, cc }))}
                placeholder="user@example.com"
              />
            </ModalField>

            <ModalField>
              <FilterLabel>BCC</FilterLabel>
              <PillEmailInput
                wide
                value={modalDraft.bcc}
                onChange={(bcc) => setModalDraft((d) => ({ ...d, bcc }))}
                placeholder="user@example.com"
              />
            </ModalField>

            <ModalActions>
              <CancelBtn onClick={() => setModalQKey(null)}>Cancel</CancelBtn>
              <SaveBtn onClick={handleModalSave} style={{ padding: "8px 18px", fontSize: 13 }}>
                Apply
              </SaveBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

export default EmailConfigureTab;
